import { createRouter, createWebHistory, type Router } from 'vue-router';
import { useSessionStore, SESSION_LOST_EVENT, CLINIC_CHANGED_ELSEWHERE_EVENT } from './session/store';
import type { VersionChecker } from './version/versionCheck';

export function buildRouter(versionChecker?: VersionChecker): Router {
  const router = createRouter({
    history: createWebHistory(),
    routes: [
      { path: '/login', name: 'login', component: () => import('./views/LoginView.vue') },
      // Olvidé mi clave (públicas). /reset-password es la ruta del enlace que envía el
      // backend (RequestPasswordReset: <base>/reset-password?token=...).
      { path: '/clave-olvidada', name: 'clave-olvidada', component: () => import('./views/ClaveOlvidadaView.vue') },
      { path: '/reset-password', name: 'clave-nueva', component: () => import('./views/ClaveNuevaView.vue') },
      {
        path: '/',
        component: () => import('./views/ShellView.vue'),
        meta: { requiresAuth: true },
        children: [
          { path: '', name: 'inicio', component: () => import('./views/InicioView.vue') },
          { path: 'hoy', name: 'hoy', component: () => import('./views/HoyView.vue') },
          { path: 'ventas', name: 'ventas', component: () => import('./views/VentasView.vue') },
          { path: 'visitas', name: 'visitas', component: () => import('./views/VisitasListView.vue') },
          { path: 'visitas/:id', name: 'visita-ficha', component: () => import('./views/VisitaFichaView.vue') },
          { path: 'tutores', name: 'tutores', component: () => import('./views/TutoresListView.vue') },
          { path: 'tutores/:id', name: 'tutor-ficha', component: () => import('./views/TutorFichaView.vue') },
          { path: 'pacientes', name: 'pacientes', component: () => import('./views/PacientesListView.vue') },
          { path: 'pacientes/:id', name: 'paciente-ficha', component: () => import('./views/PacienteFichaView.vue') },
          { path: 'resultados', name: 'resultados', component: () => import('./views/ResultadosView.vue') },
          { path: 'agenda', name: 'agenda', component: () => import('./views/AgendaView.vue') },
          { path: 'inventario', name: 'inventario', component: () => import('./views/InventarioView.vue') },
          // Copiloto v2 (plan v5.2 Fase A), vista previa: Inicio y capítulos 1 a 5.
          // El backend aplica rol financiero + feature.copiloto_v2 (403/404 si no).
          { path: 'copiloto', name: 'copiloto', component: () => import('./views/CopilotoInicioView.vue') },
          // Plan de acción (PA-1/PA-2): sugeridas del motor + acciones de la clínica.
          { path: 'copiloto/plan', name: 'copiloto-plan', component: () => import('./views/CopilotoPlanView.vue') },
          {
            path: 'copiloto/capitulo/:n(1|2|3|4|5)',
            name: 'copiloto-capitulo',
            component: () => import('./views/CopilotoCapituloView.vue'),
          },
          // Simulador "qué pasa si" (SI-1 a SI-4), mismo gate en el backend.
          { path: 'copiloto/simulador', name: 'copiloto-simulador', component: () => import('./views/CopilotoSimuladorView.vue') },
          // Mi clínica: pestañas Cargas y Morfología del Copiloto v2 (Compuerta 0).
          { path: 'mi-clinica', name: 'mi-clinica', component: () => import('./views/MiClinicaView.vue') },
          // Onboarding de clínicas (Copiloto v2 Fase C), detrás de feature.copiloto_v2 en el backend.
          // Cartera: solo la ejecutiva de onboarding (403 al resto). Resumen sin id = clínica activa.
          { path: 'onboarding', name: 'onboarding-cartera', component: () => import('./views/OnboardingCarteraView.vue') },
          { path: 'onboarding/clinica/:id?', name: 'onboarding-clinica', component: () => import('./views/OnboardingResumenView.vue') },
          { path: 'onboarding/bloques', name: 'onboarding-bloques', component: () => import('./views/OnboardingBloquesView.vue') },
        ],
      },
      { path: '/:pathMatch(.*)*', redirect: '/' },
    ],
  });

  router.beforeEach(async (to, from) => {
    // Versión nueva publicada: recargar en la navegación (momento seguro, sin
    // formularios a medio llenar en la vista que se abandona).
    if (versionChecker?.updateReady.value && from.matched.length > 0) {
      window.location.assign(to.fullPath);
      return false;
    }
    const session = useSessionStore();
    await session.bootstrap();
    if (to.matched.some((r) => r.meta.requiresAuth) && !session.isAuthenticated) {
      return { name: 'login', query: to.fullPath !== '/' ? { redirect: to.fullPath } : {} };
    }
    if (to.name === 'login' && session.isAuthenticated) return { path: '/' };
    return true;
  });

  window.addEventListener(SESSION_LOST_EVENT, () => {
    if (router.currentRoute.value.name === 'login') return;
    void router.replace({ name: 'login', query: { expirada: '1' } });
  });

  // Otra pestaña cambió de clínica: recargar desde Inicio con la sesión (cookie) ya
  // apuntando a la clínica nueva, en vez de mostrar datos de una con el nombre de otra.
  window.addEventListener(CLINIC_CHANGED_ELSEWHERE_EVENT, () => {
    window.location.assign('/');
  });

  return router;
}
